import headerImg from '@/assets/img/report_pdf/header-pdf.jpg';
import numbersFormats from '@/mixins/numbersFormats';

export default {
  mixins: [numbersFormats],

  methods: {
    /**
     * Adds the report header image and optional title to the PDF.
     *
     * @param {jsPDF} doc - PDF document instance.
     * @param {string|null} title - Optional report title.
     * @returns {number} The next vertical position after the header.
     */
    addPdfHeader(doc, title = null) {
      return new Promise((resolve) => {
        const header = new Image();

        header.onload = () => {
          const pageWidth = doc.internal.pageSize.getWidth();
          const imageRatio = header.naturalHeight / header.naturalWidth;
          const imageHeight = pageWidth * imageRatio;

          doc.addImage(header, 'jpg', 0, 0, pageWidth, imageHeight);
          doc.setFontSize(15);

          if (title) {
            doc.text(title, 40, 19);
          }

          resolve(imageHeight + 5);
        };

        header.src = headerImg;
      });
    },

    /**
     * Adds the report footer with the confidentiality disclaimer.
     *
     * @param {jsPDF} doc - PDF document instance.
     * @param {number} posY - Vertical position where the footer will be placed.
     */
    addPdfFooter(doc, posY) {
      doc.setFontSize(6);
      const pageWidth = doc.internal.pageSize.getWidth();
      const marginX = pageWidth - 19;
      doc.text(
        'Información confidencial Prohibida su reproducción o divulgación total\n o parcial, así como su uso o aprovechamiento sin autorización escrita de IUSA.',
        marginX,
        posY + 10,
        { align: 'right' }
      );
    },

    /**
     * Creates a section title above the table.
     *
     * @param {jsPDF} doc - PDF document instance.
     * @param {string} title - Section title.
     * @param {number} startY - Initial vertical position.
     */
    createTitleTable(doc, title, startY) {
      doc.autoTable({
        startY: startY,
        theme: 'plain',
        body: [
          [
            {
              content: title,
              styles: {
                halign: 'center',
                fontStyle: 'bold',
                textColor: [0, 0, 0],
              },
            },
          ],
        ],
        styles: {
          fillColor: [242, 242, 242],
          fontSize: 8,
          cellPadding: 1,
        },
      });
    },

    /**
     * Creates a table section and optionally applies custom styles
     * and currency formatting to specific columns.
     *
     * @param {jsPDF} doc - PDF document instance.
     * @param {Object} dataTable - Table configuration containing columns and rows.
     * @param {string} title - Optional section title.
     * @param {number} currentY - Current vertical position.
     * @param {Object|null} customStyles - Optional row styles.
     * @returns {number} The next vertical position after the table.
     */
    createTable(doc, dataTable, title, currentY, customStyles = null) {
      if (title) this.createTitleTable(doc, title, currentY);

      let tableBody = dataTable.rows;
      if (!tableBody || tableBody.length === 0) {
        tableBody = [
          [
            {
              content: 'Sin datos',
              colSpan: dataTable.columns.length,
              styles: { halign: 'center', fontStyle: 'italic' },
            },
          ],
        ];
      }

      doc.autoTable({
        startY: doc.lastAutoTable.finalY,
        styles: { halign: 'center', fontSize: 8, cellPadding: 1 },
        tableLineColor: [242, 242, 242],
        tableLineWidth: 0.01,
        theme: 'grid',
        columns: dataTable.columns,
        body: tableBody,
        headStyles: {
          fillColor: [242, 242, 242],
          textColor: [0, 0, 0],
          lineColor: [242, 242, 242],
          lineWidth: 0.01,
        },
        didParseCell: (data) => {
          if (data.section === 'body') {
            // Apply custom styles to the entire row.
            // Example: {fillColor: [163, 0, 20], textColor: [255, 255, 255], halign: 'right'}
            if (customStyles) {
              data.cell.styles.fillColor = customStyles.fillColor;
              data.cell.styles.textColor = customStyles.textColor;
              data.cell.styles.halign = customStyles.halign;
            }

            // Apply currency formatting to columns configured with format: 'currency'.
            // Example: { header: 'Amount', dataKey: 'total', format: 'currency' }
            // 1500.5 -> $1,500.50
            const columnConfig = data.column.raw;
            if (columnConfig?.format === 'currency') {
              const rawValue = data.cell.raw;
              if (
                rawValue !== undefined &&
                rawValue !== null &&
                rawValue !== ''
              ) {
                data.cell.text = [this.formatNumber(rawValue)];
              } else {
                data.cell.text = ['-'];
              }
            }
          }
        },
      });

      return doc.lastAutoTable.finalY + 2;
    },
  },
};
